import type { JoinPreview } from '@pages/JoinTrip/core'
import { publicApi, publicQueryRetry } from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { useQuery } from '@tanstack/react-query'

/**
 * Query options for the public invitation preview (`GET /trips/:code/joinPreview`),
 * exported so tests pin key/URL/retry without mounting the hook. Rides the
 * unauthenticated `publicApi`: the invitation must render the same signed in or
 * out, and an expired session must never bounce it to login. A deterministic
 * 4xx (unknown code) is never retried — the page shows "expired" immediately.
 */
export const joinPreviewQueryOptions = (tripCode: string | undefined) => ({
	queryKey: queryKeys.joinPreview(tripCode),
	queryFn: async (): Promise<JoinPreview> => {
		if (!tripCode) throw new Error('Trip code is required')
		const response = await publicApi.get<JoinPreview>(`/trips/${tripCode}/joinPreview`)
		return response.data
	},
	enabled: !!tripCode,
	retry: publicQueryRetry,
})

export const useJoinPreview = (tripCode: string | undefined) =>
	useQuery(joinPreviewQueryOptions(tripCode))
